import type { MediaBrandProfile, MediaBrandProfileInput } from './types';

/**
 * Shared CRUD service for media_brand_profiles.
 * All functions accept a Supabase client as the first argument so any app
 * can use them regardless of how it creates its client.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SupabaseClient = any;

export async function fetchBrandProfiles(
  supabase: SupabaseClient,
  userId: string
): Promise<MediaBrandProfile[]> {
  const { data, error } = await supabase
    .from('media_brand_profiles')
    .select('*')
    .eq('user_id', userId)
    .order('is_default', { ascending: false })
    .order('name', { ascending: true });

  if (error) {
    console.error('Fetch brand profiles error:', error);
    return [];
  }
  return (data ?? []) as MediaBrandProfile[];
}

export async function createBrandProfile(
  supabase: SupabaseClient,
  userId: string,
  input: MediaBrandProfileInput
): Promise<MediaBrandProfile> {
  if (input.is_default) {
    await supabase
      .from('media_brand_profiles')
      .update({ is_default: false })
      .eq('user_id', userId)
      .eq('is_default', true);
  }

  const { data, error } = await supabase
    .from('media_brand_profiles')
    .insert({ user_id: userId, ...input })
    .select()
    .single();

  if (error || !data) throw error ?? new Error('Failed to create brand profile');
  return data as MediaBrandProfile;
}

export async function updateBrandProfile(
  supabase: SupabaseClient,
  userId: string,
  id: string,
  input: MediaBrandProfileInput
): Promise<void> {
  if (input.is_default) {
    await supabase
      .from('media_brand_profiles')
      .update({ is_default: false })
      .eq('user_id', userId)
      .eq('is_default', true);
  }

  const { error } = await supabase
    .from('media_brand_profiles')
    .update({ ...input, updated_at: new Date().toISOString() })
    .eq('id', id)
    .eq('user_id', userId);

  if (error) throw error;
}

export async function deleteBrandProfile(
  supabase: SupabaseClient,
  userId: string,
  id: string
): Promise<void> {
  const { error } = await supabase
    .from('media_brand_profiles')
    .delete()
    .eq('id', id)
    .eq('user_id', userId);

  if (error) throw error;
}

export async function uploadBrandProfileLogo(
  supabase: SupabaseClient,
  file: File,
  userId: string,
  storageBucket = 'images'
): Promise<string> {
  const ext = file.name.split('.').pop() || 'png';
  const path = `brand-logos/${userId}/${Date.now()}.${ext}`;

  const { data, error } = await supabase.storage
    .from(storageBucket)
    .upload(path, file, { cacheControl: '31536000', upsert: false });

  if (error) throw error;

  const { data: signedData, error: signError } = await supabase.storage
    .from(storageBucket)
    .createSignedUrl(data.path, 60 * 60 * 24 * 365);

  if (signError || !signedData?.signedUrl)
    throw signError ?? new Error('Failed to create signed URL');

  return signedData.signedUrl;
}
